<template>
  <div>
    <div v-for="(section, index) in sections" :key="index" class="mb-16">
      <poppins-card class="w-full mx-auto px-16 py-24 md-1:px-24">
        <div class="flex justify-between items-center">
          <h2 class="text-h4">{{ section.title }}</h2>
          <!-- <poppins-button v-if="section.isEditable" value="Edit" variant="text" /> -->
        </div>
        <component
          :is="section.component"
          v-if="section.component"
          class="mt-16"
          v-bind="section.props"
          v-on="section.listeners"
        />
      </poppins-card>
    </div>
  </div>
</template>

<script lang="ts">
import pluralize from 'pluralize';
import { camelCase } from 'lodash';
import { FormattedCareType } from '../__types__/CareTypes';
import {
  hasExperiences,
  getExperiences,
  hasSpecialNeedsExperiences,
  getSpecialNeedsExperiences,
} from '../__types__/ExperienceTypes';
import PoppinsCard from '@/components/card/Card.vue';
import PoppinsButton from '@/components/button/Button.vue';
import ProfileBio from './profile_sections/ProfileBio.vue';
import ProfileExperience from './profile_sections/ProfileExperience.vue';
import ProfileReviews from './profile_sections/ProfileReviews.vue';
import ProfileCertifications from './profile_sections/ProfileCertifications.vue';
import ProfileRates from './profile_sections/ProfileRates.vue';
import ProfileSpecialNeedsExperience from './profile_sections/ProfileSpecialNeedsExperience.vue';
import ProfileEducation from './profile_sections/ProfileEducation.vue';
import ProfileSchedule from './profile_sections/ProfileSchedule.vue';
import ProfileCareServicesOffered from './profile_sections/ProfileCareServicesOffered.vue';

interface ProfileSection {
  title?: string;
  component?: any;
  props?: Record<string, any>;
  listeners?: Record<string, Function>;
}

export default {
  name: 'ProviderProfileSections',
  components: {
    PoppinsCard,
    PoppinsButton,
  },
  props: {
    profileAccountData: {
      default: () => ({}),
      type: Object,
    },
    reviewsData: {
      default: () => ({}),
      type: Object,
    },
    paramData: {
      type: Object,
      default: () => ({}),
    },
    profileBelongsToViewer: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    formattedCareType(): FormattedCareType {
      return FormattedCareType[this.paramData.careType.toUpperCase()];
    },

    visibleCareServices(): string[] {
      const { careType } = this.paramData;
      const { profiles } = this.profileAccountData;

      if (!profiles) return [];

      const CARE_TYPE_MAP = {
        babysitter: 'BABYSITTER',
        nanny: 'NANNY',
        petCare: 'PET_CARE',
        housekeeping: 'HOUSEKEEPING',
        companionCare: 'COMPANION_CARE',
      };

      const availableCareTypes: string[] = [];

      Object.entries(CARE_TYPE_MAP).forEach(([backendKey, componentKey]) => {
        if (backendKey === camelCase(careType)) {
          return;
        }

        const profile = profiles[backendKey];
        if (profile?.profileVisible) {
          availableCareTypes.push(componentKey);
        }
      });

      return availableCareTypes;
    },
    profileData(): any {
      return this.profileAccountData?.profiles?.[this.formattedCareType]?.profile || {};
    },
    sections(): ProfileSection[] {
      const sections = [
        this.getBioSection(),
        this.getExperienceSection(),
        this.getReviewsSection(),
        { title: 'Trust & Safety' },
        this.getCertificationsSection(),
        this.getScheduleSection(),
        this.getRatesSection(),
        { title: 'Other services' },
        { title: 'Specialty skills' },
        this.getSpecialNeedsExperienceSection(),
        this.getEducationSection(),
        { title: 'Activity-inspired care' },
        this.getCareServicesOfferedSection(),
      ];

      return sections.filter((section) => section !== null) as ProfileSection[];
    },
  },
  methods: {
    getBioSection(): ProfileSection | null {
      const { bio } = this.profileData;
      if (!bio) return null;
      return {
        title: `About ${this.profileAccountData?.displayName}`,
        component: ProfileBio,
        props: { bio },
      };
    },
    getExperienceSection(): ProfileSection | null {
      const { yearsExperience } = this.profileData;

      if (!hasExperiences(this.profileData, this.formattedCareType)) return null;

      const experienceSectionTitle =
        this.formattedCareType === 'companionCare' || yearsExperience === 0
          ? 'Offers care for'
          : `${yearsExperience} ${pluralize('year', yearsExperience)} of experience`;

      return {
        title: experienceSectionTitle,
        component: ProfileExperience,
        props: {
          careType: this.formattedCareType,
          ...getExperiences(this.profileData, this.formattedCareType),
        },
      };
    },
    getReviewsSection(): ProfileSection | null {
      if (!this.reviewsData) return null;
      return {
        title: 'Ratings & Reviews',
        component: ProfileReviews,
        props: {
          reviewsData: this.reviewsData,
          profileAccountData: this.profileAccountData,
          profileBelongsToViewer: this.profileBelongsToViewer,
        },
        listeners: {
          'view-more-reviews': () => this.$emit('view-more-reviews'),
        },
      };
    },
    getCertificationsSection(): ProfileSection | null {
      const { badges } = this.profileData;
      if (!badges) return null;
      return {
        title: 'Certifications',
        component: ProfileCertifications,
        props: { badges },
      };
    },
    getScheduleSection(): ProfileSection | null {
      const { generalAvailability, availabilityValidUntil } = this.profileData;
      if (!generalAvailability) return null;
      return {
        title: 'Schedule',
        component: ProfileSchedule,
        props: { schedule: generalAvailability, sitterName: this.profileAccountData?.displayName, availabilityValidUntil },
      };
    },
    getRatesSection(): ProfileSection | null {
      // Use either 'rates' or 'payRate'
      const { payRate, rates } = this.profileData;
      const effectiveRates = rates;
      if (!payRate && !effectiveRates) return null;
      return {
        title: 'Rates',
        component: ProfileRates,
        props: {
          careType: this.formattedCareType,
          rates: { payRate, rates: effectiveRates },
          profile: this.profileData,
        },
      };
    },
    getSpecialNeedsExperienceSection(): ProfileSection | null {
      if (!hasSpecialNeedsExperiences(this.profileData, this.formattedCareType)) return null;

      return {
        title: 'Special needs experience with',
        component: ProfileSpecialNeedsExperience,
        props: {
          ...getSpecialNeedsExperiences(this.profileData, this.formattedCareType),
        },
      };
    },
    getEducationSection(): ProfileSection | null {
      const { education } = this.profileData;
      if (!education) return null;
      return {
        title: 'Education',
        component: ProfileEducation,
        props: { careType: this.formattedCareType, education },
      };
    },
    getCareServicesOfferedSection(): ProfileSection | null {
      if (!this.visibleCareServices.length) return null;
      return {
        title: 'Additional care offered',
        component: ProfileCareServicesOffered,
        props: {
          visibleCareServices: this.visibleCareServices,
          sitterUuid: this.paramData.sitterUuid,
        },
      };
    },
  },
};
</script>
